/**
 * <cart-drawer> — Section Rendering API ile sepet drawer.
 * Global event'leri dinler:
 *   - 'cart:open'  → drawer'ı aç
 *   - 'cart:added' → drawer'ı yeniden render et + aç + bubble güncelle
 *
 * UTM cookie'leri ATC sırasında cart.attributes'a yazılır (ACTMS attribution için).
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('[data-cd-dialog]');
    this.body = this.querySelector('[data-cd-body]');
    this.closeBtn = this.querySelector('[data-cd-close]');

    this.closeBtn?.addEventListener('click', () => this.dialog?.close());
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });

    this.addEventListener('click', (e) => this.handleClick(e));
    this.addEventListener('change', (e) => this.handleChange(e));

    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:added', (e) => this.onAdded(e.detail));
  }

  open() {
    this.dialog?.showModal();
  }

  async onAdded(detail) {
    if (detail?.sections) {
      this.replaceSelf(detail.sections);
    }
    this.open();
  }

  replaceSelf(sections) {
    const html = sections['cart-drawer'];
    if (!html) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const next = tmp.querySelector('cart-drawer');
    if (next) {
      this.replaceWith(next);
      next.open();
    }
  }

  async refreshCart() {
    const res = await fetch('/?sections=cart-drawer,cart-icon-bubble');
    if (!res.ok) return;
    const sections = await res.json();
    if (window.refreshCartBubble) window.refreshCartBubble(sections);
    this.replaceSelf(sections);
  }

  async updateQty(key, qty) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty, sections: 'cart-drawer,cart-icon-bubble' }),
    });
    if (!res.ok) return;
    const data = await res.json();
    if (window.refreshCartBubble) window.refreshCartBubble(data.sections);
    this.replaceSelf(data.sections);
  }

  handleClick(e) {
    const item = e.target.closest('[data-cd-item]');
    if (!item) return;
    const key = item.dataset.key;

    if (e.target.closest('[data-cd-remove]')) {
      this.updateQty(key, 0);
      return;
    }
    if (e.target.closest('[data-cd-decrement]')) {
      const input = item.querySelector('[data-cd-qty]');
      const next = Math.max(0, parseInt(input.value, 10) - 1);
      this.updateQty(key, next);
      return;
    }
    if (e.target.closest('[data-cd-increment]')) {
      const input = item.querySelector('[data-cd-qty]');
      const next = parseInt(input.value, 10) + 1;
      this.updateQty(key, next);
      return;
    }
  }

  handleChange(e) {
    if (e.target.matches('[data-cd-qty]')) {
      const item = e.target.closest('[data-cd-item]');
      if (!item) return;
      const qty = Math.max(0, parseInt(e.target.value, 10) || 0);
      this.updateQty(item.dataset.key, qty);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

/**
 * Global ATC interceptor — product-main form submit'ini yakalar,
 * /cart/add.js çağırır, sections döndürür, cart:added event'i fire eder.
 */
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('[data-pm-form]');
  if (!form) return;
  e.preventDefault();

  const submit = form.querySelector('[data-pm-atc]');
  const originalText = submit?.querySelector('[data-pm-atc-text]')?.textContent;
  if (submit) {
    submit.disabled = true;
    submit.querySelector('[data-pm-atc-text]').textContent = 'Ekleniyor…';
  }

  // UTM cookies → cart attributes
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const fd = new FormData(form);
  utmKeys.forEach((k) => {
    const match = document.cookie.match(new RegExp('(^| )' + k + '=([^;]+)'));
    if (match) fd.append(`attributes[landing_${k}]`, decodeURIComponent(match[2]));
  });

  // cart-icon-bubble snippet'tir, section değil — sadece cart-drawer iste
  fd.append('sections', 'cart-drawer');
  fd.append('sections_url', window.location.pathname);

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: fd,
    });
    if (!res.ok) {
      // Shopify hata response'unu detaylı oku ve raporla
      const text = await res.text();
      let errMsg;
      try {
        const err = JSON.parse(text);
        errMsg = err.description || err.message || `${err.status || res.status}: ${text.slice(0, 200)}`;
      } catch {
        errMsg = `${res.status} ${res.statusText} — ${text.slice(0, 200)}`;
      }
      console.error('[cart/add.js] Hata:', res.status, errMsg, '\nForm data:', Object.fromEntries(fd));
      throw new Error(errMsg);
    }
    const data = await res.json();
    console.log('[cart/add.js] OK:', data);

    // Cart bubble'ı fresh cart.js çağrısıyla güncelle (cart-icon-bubble snippet için)
    fetch('/cart.js').then((r) => r.json()).then((cart) => {
      const bubble = document.querySelector('.cart-bubble__count');
      if (bubble) {
        bubble.dataset.count = cart.item_count;
        bubble.textContent = cart.item_count;
      }
    });

    // drawer'ı bilgilendir
    document.dispatchEvent(new CustomEvent('cart:added', { detail: { sections: data.sections } }));
  } catch (err) {
    console.warn('Add to cart failed:', err);
    alert(`Sepete eklenemedi: ${err.message || 'Bilinmeyen hata'}`);
  } finally {
    if (submit) {
      submit.disabled = false;
      if (originalText) submit.querySelector('[data-pm-atc-text]').textContent = originalText;
    }
  }
});
